import { CommonModule } from '@angular/common';
import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, ElementRef, HostListener } from '@angular/core';
import { AgGridModule } from 'ag-grid-angular';
import { ColDef, GridOptions, GridApi, CellClickedEvent } from 'ag-grid-community';
import { TableData, TableRow } from '../report-table/report-table.component';
import { EllipsisCellComponent } from '../ellipsis-cell/ellipsis-cell.component';

type Row = Record<string, any>;
type Header = Record<string, any>;
type SelPoint = { rowIndex: number; colId: string };

@Component({
  selector: 'ngx-report-table-v3',
  templateUrl: './report-table-v3.component.html',
  standalone: true,
  imports: [CommonModule, AgGridModule], // <- REQUIRED
  styleUrls: ['./report-table-v3.component.scss'],
})

export class ReportTableV3Component implements OnInit {

  constructor(protected el: ElementRef) { }

  protected gridApi: GridApi;

  defaultColDef: ColDef = {
    resizable: true,
    sortable: true,
    minWidth: 70,
    comparator: compareReportCells,
    tooltipValueGetter: (params: any) => {
      const v = params.value;
      if (v == null) return undefined;
      if (typeof v !== 'object') return String(v) || undefined;
      const text = String(v.value ?? v.tooltip ?? '');
      return text || undefined;
    },
  };

  onGridReady(e: any) {
    this.gridApi = e.api;
    this.applyAutoSize();
  
    const defs = this.gridApi.getColumnDefs() as ColDef[];
    const existingCellClass = (d: ColDef) => 
      typeof d.cellClass === 'function' ? d.cellClass : 
      (d.cellClass ? () => d.cellClass : undefined);
    
    const patched = defs.map((d) => {
      const orig = existingCellClass(d);
      return {
        ...d,
        cellClass: (p: any) => {
          const base = orig ? (typeof orig === 'function' ? orig(p) : orig) : '';
          const sel = this.isCellInSelection(p.rowIndex, p.column.getColId()) ? 'my-range-selected' : '';
          return [base, sel].filter(Boolean).join(' ');
        },
      };
    });
    this.gridApi.setGridOption('columnDefs', patched);
  }
  onFirstDataRendered(params: any) {

    this.applyAutoSize();
  }

  protected applyAutoSize() {
    if (this.gridApi && this.tableData && this.tableData.headers) {
      const autoSizeKeys = Object.keys(this.tableData.headers).filter(key =>
        this.tableData.headers[key]['autoSize'] === true
      );

      if (autoSizeKeys.length > 0) {
        // Use a small timeout to ensure rendering is complete even after event
        setTimeout(() => {
          this.gridApi.autoSizeColumns(autoSizeKeys, false); // false = include headers (default), true = skip headers

          // Check if we need to expand columns to fill the screen
          const allDisplayedCols = this.gridApi.getAllDisplayedColumns();
          if (!allDisplayedCols || allDisplayedCols.length === 0) return;

          const totalWidth = allDisplayedCols.reduce((acc, col) => acc + col.getActualWidth(), 0);

          // Using ElementRef to get the specific grid viewport width
          const gridElement = this.el.nativeElement.querySelector('.ag-body-viewport') as HTMLElement;
          const viewportWidth = gridElement ? gridElement.clientWidth : 0;

          if (viewportWidth > 0 && totalWidth < viewportWidth) {
            this.gridApi.sizeColumnsToFit();
          }
        }, 300);
      }
    }
  }

  processUnpinnedColumns(p: any) {
    // console.log("processUnpinnedColumns", p);
    return [];
  }
  getRowId = (p: any) =>
    `${p.data.facilityId}:${p.data.firstName}:${p.data.lastName}:${p.data.startDate ?? p.data.admissionDate}`;


  @Input() gridOptions: GridOptions;
  @Input() tableData: TableData;
  @Input() pinnedLeftNumOfColumns: number;
  @Input() cellSelection = true;
  @Output() cellClick = new EventEmitter<{ colId: string; rowIndex: number; rowData: any }>();
  rows: Row[];
  pinnedBottomRows: Row[] = [];
  columns: ColDef[];

  private anchor?: SelPoint;      // first click
  private selStart?: SelPoint;
  private selEnd?: SelPoint;
  private isDragging = false;

  ngOnInit(): void {
    this.updateTableData();
    if (!this.gridOptions) {
      this.gridOptions = {

      }
    }
    if (!this.gridOptions.rowHeight) {
      this.gridOptions.rowHeight = 34;
    }
    if (!this.gridOptions.headerHeight) {
      this.gridOptions.headerHeight = 34;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['tableData'] && !changes['tableData'].firstChange) {
      this.updateTableData();
    }
  }

  protected updateTableData(): void {
    if (!this.tableData) {
      return;
    }

    const tableHeaders = this.tableData.headers;
    const orderedHeaders = getOrderedHeaders(tableHeaders);
    const rows = this.tableData.rows ?? [];
    const lastRow = rows[rows.length - 1];
    // the totals row is appended as a plain row by every report; it must not take part in sorting
    const hasTotalsRow = !!lastRow && TOTALS_LABELS.includes(String(cellSortValue(lastRow[orderedHeaders[0]])));
    this.rows = hasTotalsRow ? rows.slice(0, -1) : rows;
    this.pinnedBottomRows = hasTotalsRow ? [lastRow] : [];
    const columnSeparators = (this.tableData as any).columnSeparators === true;
    this.columns = orderedHeaders.map((header, index) => {
      const headerConfig = tableHeaders[header as keyof typeof tableHeaders];
      const specifiedWidth = headerConfig['width'];
      const isBoldLeft = (headerConfig as any)['boldLeftBorder'] === true;
      // Thin separator between every column (except the very first); bold separator
      // between monitoring check groups. Only active when columnSeparators is set.
      const borderClass = !columnSeparators || index === 0
        ? ''
        : isBoldLeft ? 'report-col-border-bold' : 'report-col-border';
      if (headerConfig['color']) {
        const style = document.createElement('style');
        const key = `list-ad-grid-col-${header}`;
        style.textContent = `.${key} { color: ${headerConfig['color']}; }`;
        document.body.appendChild(style);
      }

      return {
        field: header,
        headerName: tableHeaders[header as keyof typeof tableHeaders]['label'] || '',
        valueGetter: (p: any) => {
          // console.log("valueGetter", p);
          // check if data string
          if (typeof p.data[header as keyof typeof p.data] === 'string') {
            return p.data[header as keyof typeof p.data];
          }
          // console.log(p.data[header as keyof typeof p.data]);
          return p.data[header as keyof typeof p.data];
        },
        cellStyle: (p: any) => {
          const colSettings: TableRow = tableHeaders[header as keyof typeof tableHeaders];
          const cell = p.data[header as keyof typeof p.data];
          const obj: any = {};
          if (colSettings?.bgColor) {
            obj.backgroundColor = colSettings.bgColor;
          }
          if (colSettings?.color) {
            obj.color = colSettings.color;
          }
          if (colSettings?.textAlign) {
            obj.textAlign = colSettings.textAlign;
          }
          // Cell-level overrides (when cell is an object with style properties)
          if (cell && typeof cell === 'object') {
            if (cell.bgColor) obj.backgroundColor = cell.bgColor;
            if (cell.color) obj.color = cell.color;
            if (cell.textAlign) obj.textAlign = cell.textAlign;
          }
          return Object.keys(obj).length > 0 ? obj : undefined;
        },
        headerClass: [
          headerConfig?.['color'] ? `list-ad-grid-col-${header}` : '',
          borderClass,
        ].filter(Boolean).join(' ') || undefined,
        cellClass: borderClass || undefined,
        //   cellRenderer: (p: any) => {
        //     // console.log("cellRenderer", p);      // check if data string
        //     if (typeof p.data[header as keyof typeof p.data] === 'string') {
        //       return p.data[header as keyof typeof p.data];
        //     }
        //     // console.log(p.data[header as keyof typeof p.data]);
        //     return p.data[header as keyof typeof p.data]?.value || '';

        //   },
        cellRenderer: EllipsisCellComponent,
        cellRendererParams: { header: headerConfig },
        pinned: this.pinnedLeftNumOfColumns && index < this.pinnedLeftNumOfColumns ? 'left' : null,
        width: specifiedWidth || undefined,
        minWidth: undefined,
        flex: this.pinnedLeftNumOfColumns && index < this.pinnedLeftNumOfColumns ? undefined : (specifiedWidth || 1)
      };
    });

    // this.applyAutoSize();
  }

  @HostListener('window:mouseup')
  onMouseUp() {
    this.isDragging = false;
  }

  @HostListener('document:mousedown', ['$event'])
  onHostMouseDown(ev: MouseEvent) {
    if (!this.cellSelection) return;
    if (!this.el.nativeElement.contains(ev.target as Node)) {
      this.clearSelection();
      return;
    }
    const cellEl = (ev.target as Element)?.closest?.('.ag-cell');
    if (!cellEl || !this.gridApi) return;
    const rowEl = cellEl.closest('.ag-row');
    if (!rowEl || rowEl.closest('.ag-floating-bottom')) return;
    const rowIndex = parseInt(rowEl.getAttribute('row-index') ?? '', 10);
    const colId = cellEl.getAttribute('col-id') ?? '';
    if (isNaN(rowIndex) || !colId) return;
    const point: SelPoint = { rowIndex, colId };
    this.anchor = point;
    this.selStart = point;
    this.selEnd = point;
    this.isDragging = true;
    this.refresh();
  }

  @HostListener('document:mousemove', ['$event'])
  onMouseMove(ev: MouseEvent) {
    if (!this.cellSelection || !this.isDragging || !this.anchor || !this.gridApi) return;
    if (!this.el.nativeElement.contains(ev.target as Node)) return;
    const cellEl = (ev.target as Element)?.closest?.('.ag-cell');
    if (!cellEl) return;
    const rowEl = cellEl.closest('.ag-row');
    if (!rowEl) return;
    const rowIndex = parseInt(rowEl.getAttribute('row-index') ?? '', 10);
    const colId = cellEl.getAttribute('col-id') ?? '';
    if (isNaN(rowIndex) || !colId) return;
    this.selStart = this.anchor;
    this.selEnd = { rowIndex, colId };
    this.refresh();
  }

  onCellClicked(e: CellClickedEvent) {
    if (e.rowIndex == null || !e.column || e.rowPinned) return;
    const point: SelPoint = { rowIndex: e.rowIndex, colId: e.column.getColId() };
    if (this.cellSelection) {
      this.anchor = point;
      this.selStart = point;
      this.selEnd = point;
      this.refresh();
    }
    this.cellClick.emit({ colId: point.colId, rowIndex: e.rowIndex, rowData: e.data });
  }

  @HostListener('window:keydown', ['$event'])
  async onKeyDown(ev: KeyboardEvent) {
    const isCopy = (ev.ctrlKey || ev.metaKey) && ev.key.toLowerCase() === 'c';
    if (!isCopy || !this.gridApi || !this.selStart || !this.selEnd) return;

    ev.preventDefault();
    const tsv = this.buildSelectionTSV();
    if (!tsv) return;

    try {
      await navigator.clipboard.writeText(tsv);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = tsv;
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
  }

  private clearSelection() {
    this.anchor = undefined;
    this.selStart = undefined;
    this.selEnd = undefined;
    this.isDragging = false;
    this.refresh();
  }

  private refresh() {
    this.gridApi?.refreshCells({ force: true });
  }

  private isCellInSelection(rowIndex: number, colId: string): boolean {
    if (!this.selStart || !this.selEnd || !this.gridApi) return false;

    const rowMin = Math.min(this.selStart.rowIndex, this.selEnd.rowIndex);
    const rowMax = Math.max(this.selStart.rowIndex, this.selEnd.rowIndex);

    const cols = this.gridApi.getAllDisplayedColumns();
    const colIds = cols.map((c) => c.getColId());

    const startIdx = colIds.indexOf(this.selStart.colId);
    const endIdx = colIds.indexOf(this.selEnd.colId);
    if (startIdx < 0 || endIdx < 0) return false;

    const colMin = Math.min(startIdx, endIdx);
    const colMax = Math.max(startIdx, endIdx);

    const thisIdx = colIds.indexOf(colId);

    return rowIndex >= rowMin && rowIndex <= rowMax && thisIdx >= colMin && thisIdx <= colMax;
  }

  /** Converts cell value to display string (handles objects like { value, numFmt, addData }) */
  private valueToDisplayString(v: any): string {
    if (v == null) return '';
    if (typeof v !== 'object') return String(v);
    let s = String(v.value ?? v.tooltip ?? '');
    if (v.numFmt === '#,##0') {
      s = s.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }
    if (v.addData) s += v.addData;
    return s;
  }

  private buildSelectionTSV(): string {
    if (!this.gridApi || !this.selStart || !this.selEnd) return '';

    const cols = this.gridApi.getAllDisplayedColumns();
    const colIds = cols.map((c) => c.getColId());

    const startIdx = colIds.indexOf(this.selStart.colId);
    const endIdx = colIds.indexOf(this.selEnd.colId);

    const colMin = Math.min(startIdx, endIdx);
    const colMax = Math.max(startIdx, endIdx);
    const selectedCols = cols.slice(colMin, colMax + 1);

    const rowMin = Math.min(this.selStart.rowIndex, this.selEnd.rowIndex);
    const rowMax = Math.max(this.selStart.rowIndex, this.selEnd.rowIndex);

    const lines: string[] = [];
    for (let r = rowMin; r <= rowMax; r++) {
      const rowNode = this.gridApi.getDisplayedRowAtIndex(r);
      if (!rowNode) continue;

      const values = selectedCols.map((col) => {
        const v = this.gridApi!.getValue(col, rowNode);
        const str = this.valueToDisplayString(v);
        return str.replace(/\r?\n/g, ' ');
      });

      lines.push(values.join('\t'));
    }

    return lines.join('\n');
  }


}

const TOTALS_LABELS = ['Totals', 'Total'];

export function cellSortValue(cell: any): string | number {
  const raw = cell && typeof cell === 'object' ? cell.value : cell;
  if (raw == null || raw === '') return '';
  if (typeof raw === 'number') return raw;
  const text = String(raw).trim();
  const numeric = Number(text.replace(/[,%$\s]/g, ''));
  return text !== '' && Number.isFinite(numeric) ? numeric : text;
}

export function compareReportCells(a: any, b: any): number {
  const va = cellSortValue(a);
  const vb = cellSortValue(b);
  if (va === '' && vb === '') return 0;
  if (va === '') return -1;
  if (vb === '') return 1;
  if (typeof va === 'number' && typeof vb === 'number') return va - vb;
  return String(va).localeCompare(String(vb), 'en', { numeric: true, sensitivity: 'base' });
}

function getOrderedHeaders(headers: Record<string, { label: string, index?: number }>) {
  const entries = Object.keys(headers);

  // 1) keys עם index
  const withIndex = entries.filter(k => headers[k].index !== undefined);

  // 2) keys בלי index
  const withoutIndex = entries.filter(k => headers[k].index === undefined);

  // 3) בניית מערך מיקומים
  const result = [];

  // שים לפי index (0-based)
  withIndex.forEach(k => {
    result[headers[k].index] = k;
  });

  // 4) מלא את החורים לפי סדר הכנסת שאר המפתחות
  withoutIndex.forEach(k => {
    let i = 0;
    while (result[i] !== undefined) i++;
    result[i] = k;
  });
  console.log(result);
  return result;


  
}